import AspectRatio from '@/components/basic/aspect-ratio';
import React, {
  useMemo,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';
import {vipOptionsV2, WeeklyCardType} from './vip.variable';
import {Trans, useTranslation} from 'react-i18next';
import {Button, ProgressBar} from 'antd-mobile';
import styles from './vip.module.scss';
import {cn, goTo, toPriceStr} from '@/utils';
import {
  getVipConfigListV2,
  VipConfigVO,
  weekCardReceive,
  weekRewardReceive,
} from './vip.service';
// import globalStore from '@/services/global.state';
import {VipMaxLevelContext} from './vip-card';
import {canUseType} from './week-card';
import WeeklyCardReceiving, {
  WeeklyCardReceivingRef,
  WeeklyCardReward,
} from './modal/weekly-card-reward';
import {useFocusEffect} from '@react-navigation/native';
import WeeklyReward, {WeeklyRewardRef} from './modal/weekly-reward';
import {getCurrentVip} from '@/utils/vip';

export interface VipInfo {
  level: number;
  // 满级时为空
  currentValue?: number; // 当前等级经验值
  nextValue?: number; // 下一等级所需经验值
}

const VipHalfCard = forwardRef<
  {init: () => void},
  {login: boolean; onVipUpdate?: (vip: VipInfo) => void}
>(({login, onVipUpdate}, ref) => {
  const [maxLevel, setMaxLevel] = useState(vipOptionsV2.length - 1);
  const [lastVip, setLastVip] = useState<VipConfigVO | null>(null);
  const [vip, setVip] = useState<VipInfo>({
    level: 0,
    currentValue: 0,
    nextValue: 0,
  });
  const {level, currentValue, nextValue} = vip;
  // const [vipRedPoint, setVipRedPoint] = useState(false);
  // useEffect(() => {
  //   const vipSub = globalStore.vipRedPointSubject.subscribe(res => {
  //     console.log('vipRedPoint', res);
  //     setVipRedPoint(res);
  //   });
  //   return () => {
  //     vipSub.unsubscribe();
  //   };
  // }, []);

  const weeklyCardRef = useRef<WeeklyCardReceivingRef>(null);

  const handleShowWeeklyCard = () => {
    weekCardReceive().then(res => {
      const prizeList = (res || [])
        .filter(
          v =>
            v.bonusAmount &&
            v.bonusAmount > 0 &&
            (v.scratchCount || v.scratchDay) &&
            (v.spinCount || v.spinDay),
        )
        .map(v => {
          const cardType = canUseType.find(t =>
            v.cardName?.toLowerCase().includes(t),
          ) as WeeklyCardType | undefined;
          if (!cardType) {
            return null;
          }
          return {
            cardType,
            money: v.bonusAmount || 0,
            scratchCard: v.scratchCount || 0,
            scratchCount: v.scratchDay || 0,
            spin: v.spinCount || 0,
            spinCount: v.spinDay || 0,
          };
        });
      const filteredPrizeList = prizeList.filter(
        v => !!v,
      ) as WeeklyCardReward[];
      if (filteredPrizeList.length > 0) {
        weeklyCardRef.current?.show(filteredPrizeList);
      }
    });
  };

  const init = useCallback(() => {
    getVipConfigListV2().then(res => {
      const list = res.sort((a, b) => (a.level || 0) - (b.level || 0));
      const baseExp = localStorage.getItem('vipBaseExp');
      if (baseExp) {
        localStorage.removeItem('vipBaseExp');
        let {currentVip, lastVip: _lastVip} = getCurrentVip(
          list,
          true,
          +baseExp,
        );
        setVip({
          level: currentVip.level || 0,
          currentValue: +baseExp,
          nextValue: currentVip.nextLevelExperience || 0,
        });
        setLastVip(_lastVip);
        setTimeout(() => {
          // setLight(true);
          ({currentVip, lastVip: _lastVip} = getCurrentVip(list));
          setVip({
            level: currentVip?.level || 0,
            currentValue: currentVip?.currentExperience || 0,
            nextValue: currentVip?.nextLevelExperience || 0,
          });
          setLastVip(_lastVip);
        }, 200);
        return;
      }
      let {currentVip, lastVip: _lastVip} = getCurrentVip(list);
      setMaxLevel(list?.[list.length - 1]?.level || 0);
      setVip({
        level: currentVip?.level || 0,
        currentValue: currentVip?.currentExperience || 0,
        nextValue: currentVip?.nextLevelExperience || 0,
      });
      setLastVip(_lastVip);
    });
  }, []);

  // 考虑到外部主动调用init，这边不需要useEffect方式调用
  useImperativeHandle(ref, () => ({
    init,
  }));

  useFocusEffect(
    useCallback(() => {
      if (!login) {
        return;
      }
      handleShowWeeklyCard();
    }, [login]),
  );

  const weeklyRewardRef = useRef<WeeklyRewardRef>(null);

  const handleShowWeeklyReward = () => {
    weekRewardReceive().then(res => {
      if (res && res.weekBaseBonusAmount && res.bonusAmount) {
        weeklyRewardRef.current?.show({
          baseMoney: res.weekBaseBonusAmount || 0,
          multiple: res.multiple || 0,
          bonusMoney: res.bonusAmount || 0,
        });
      }
    });
  };

  useFocusEffect(
    useCallback(() => {
      if (!login) {
        return;
      }
      handleShowWeeklyReward();
    }, [login]),
  );

  useEffect(() => {
    onVipUpdate?.(vip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vip]);

  const {background, sign, titleClass, curTextClass, progressLinear} =
    useMemo(() => {
      if (!vipOptionsV2[level]) {
        return vipOptionsV2[0];
      }
      return vipOptionsV2[level];
    }, [level]);
  const {t} = useTranslation();

  const toVip = () => {
    goTo('Vip');
  };
  const nowDiff = useMemo(() => {
    if (currentValue) {
      return Math.max(
        Number(currentValue) - Number(lastVip?.nextLevelExperience || 0),
        0,
      );
    }
    return 0;
  }, [currentValue, lastVip]);
  const nextDiff = useMemo(() => {
    if (nextValue) {
      return Math.max(
        Number(nextValue) - Number(lastVip?.nextLevelExperience || 0),
        0,
      );
    }
    return 1;
  }, [nextValue, lastVip]);
  return (
    <div
      className="w-full h-[7.0625rem] relative overflow-hidden"
      onClick={toVip}>
      <AspectRatio
        className="absolute top-2 left-0 right-0 z-[0]"
        ratio={336 / 168}>
        <div
          style={{
            backgroundImage: `url(${background})`,
          }}
          className={cn('size-full bg-full bg-no-repeat')}
        />
      </AspectRatio>
      <VipMaxLevelContext.Provider value={maxLevel}>
        <div className="flex flex-col px-3 pt-5 z-[1]">
          <div className="flex flex-row items-end gap-3 z-[1]">
            <div className={'size-9 relative'}>
              <div
                style={{
                  backgroundImage: `url(${sign})`,
                }}
                className="absolute -left-2 -right-2 -bottom-4 top-0 bg-full bg-no-repeat"
              />
            </div>
            <div
              className={cn(
                'flex flex-col mb-1 gap-0.5 flex-1',
                styles['vip-progress'],
              )}>
              <div className={'flex flex-row justify-between'}>
                <div className="red-point-empty" />
                {level + 1 <= maxLevel && (
                  <span
                    className={cn(
                      'font-bold text-[1.125rem] font-din',
                      titleClass,
                    )}>
                    V{level + 1}
                  </span>
                )}
              </div>
              <ProgressBar
                style={{
                  '--fill-color': progressLinear,
                }}
                percent={
                  currentValue && nextValue ? (nowDiff / nextDiff) * 100 : 0
                }
              />
            </div>
          </div>
          <div className="py-1 flex flex-row items-center justify-between z-[1]">
            <div className={cn('text-[0.625rem] flex-1', curTextClass)}>
              {vip.level != null && vip.level < maxLevel ? (
                <Trans
                  i18nKey="vip.rechargeTip"
                  components={[<span className="font-bold text-[#F88B1D]" />]}
                  values={{
                    price: toPriceStr(
                      vip.nextValue && vip.currentValue != null
                        ? Math.max(
                            Number(vip.nextValue) - Number(vip.currentValue),
                            0,
                          )
                        : 0,
                      {
                        fixed: 0,
                      },
                    ),
                    level: `VIP${(vip.level || 0) + 1}`,
                  }}
                />
              ) : (
                t('vip.incoming')
              )}
            </div>
            <Button
              className="px-3 h-6 bg-c1 text-white text-[0.625rem] p-0 text-center justify-center items-center rounded-full before:rounded-full shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25),0px_1px_0px_0px_rgba(255,255,255,0.20)_inset]"
              onClick={() => {
                if (!login) {
                  goTo('Login');
                  return;
                }
                goTo('Recharge');
              }}>
              {t('label.recharge')}
            </Button>
          </div>
        </div>
      </VipMaxLevelContext.Provider>
      <WeeklyCardReceiving ref={weeklyCardRef} />
      <WeeklyReward ref={weeklyRewardRef} />
    </div>
  );
});

export default VipHalfCard;
